"use client";

import { Flag } from "@/components/shared/flags";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, User, Target, Users, Activity } from "lucide-react";
import { motion } from "framer-motion";
import type { HracKomplet } from "@/types/database";
import type { Krajina } from "@/data/skupiny";
import { cn } from "@/lib/utils";

interface SovenskoReprezentaciaProps {
  krajina: Krajina;
  nominacia: HracKomplet[];
}

const CARD_CLASSES = cn(
  "group relative overflow-hidden border-muted/20 bg-transparent rounded-2xl shadow-sm",
  "hover:shadow-xl transition-all duration-300 bg-background/50 backdrop-blur-sm",
);

const HEADER_CLASSES = cn(
  "text-3xl md:text-5xl font-black tracking-tighter uppercase text-foreground drop-shadow-sm",
);

export function SovenskoReprezentacia({
  krajina,
  nominacia,
}: SovenskoReprezentaciaProps) {
  const SECTION_TITLE = "Slovenská reprezentácia";
  const SECTION_DESC = "Oficiálna nominácia Slovenska na ZOH 2026 v Miláne.";

  // Zoskupiť hráčov podľa pozície
  const brankari = nominacia.filter((h) => h.pozicia === "brankar");
  const obrancovia = nominacia.filter((h) => h.pozicia === "obranca");
  const utocnici = nominacia.filter((h) => h.pozicia === "utocnik");

  const getPoziciaLabel = (pozicia: string) => {
    switch (pozicia) {
      case "brankar":
        return "Brankári";
      case "obranca":
        return "Obrancovia";
      case "utocnik":
        return "Útočníci";
      default:
        return pozicia;
    }
  };

  const getPoziciaIcon = (pozicia: string) => {
    switch (pozicia) {
      case "brankar":
        return <Shield className="h-4 w-4" />;
      case "obranca":
        return <Shield className="h-4 w-4" />;
      case "utocnik":
        return <Target className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="space-y-8"
    >
      {/* Header */}
      <div className="text-center space-y-6 mb-12">
        <div className="flex items-center justify-center gap-4 mb-4">
          <Flag kod={krajina.kod} size={48} className="shadow-sm rounded" />
          <h2 className={HEADER_CLASSES}>{SECTION_TITLE}</h2>
          <Flag kod={krajina.kod} size={48} className="shadow-sm rounded" />
        </div>

        <p className="text-muted-foreground font-medium max-w-2xl mx-auto leading-relaxed">
          {SECTION_DESC}
        </p>
      </div>

      {/* Štatistiky tímu */}
      <Card className={CARD_CLASSES}>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-3 text-xl font-black uppercase tracking-widest">
            <Users className="h-5 w-5 text-primary" />
            Štatistiky tímu
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-muted/30 rounded-xl">
              <div className="text-2xl font-black text-foreground">
                {nominacia.length}
              </div>
              <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground/60">
                Hráčov
              </div>
            </div>
            <div className="text-center p-4 bg-muted/30 rounded-xl">
              <div className="text-2xl font-black text-foreground">
                {brankari.length}
              </div>
              <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground/60">
                Brankárov
              </div>
            </div>
            <div className="text-center p-4 bg-muted/30 rounded-xl">
              <div className="text-2xl font-black text-foreground">
                {obrancovia.length}
              </div>
              <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground/60">
                Obrancov
              </div>
            </div>
            <div className="text-center p-4 bg-muted/30 rounded-xl">
              <div className="text-2xl font-black text-foreground">
                {utocnici.length}
              </div>
              <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground/60">
                Útočníkov
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Zoznam hráčov podľa pozícií */}
      <div className="space-y-8">
        {[brankari, obrancovia, utocnici].map((hraci, index) => {
          if (hraci.length === 0) return null;

          const pozicia = hraci[0].pozicia;
          const poziciaLabel = getPoziciaLabel(pozicia);

          return (
            <motion.div
              key={pozicia}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className={CARD_CLASSES}>
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-3 text-xl font-black uppercase tracking-widest">
                    {getPoziciaIcon(pozicia)}
                    {poziciaLabel} ({hraci.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {hraci.map((hrac) => (
                      <div
                        key={hrac.id}
                        className="p-4 bg-muted/20 rounded-xl border border-muted/10 hover:bg-muted/30 transition-colors"
                      >
                        <div className="space-y-2">
                          <div className="font-bold text-foreground truncate">
                            {hrac.cele_meno}
                          </div>
                          <div className="text-sm text-muted-foreground/80">
                            {hrac.klub && <div>{hrac.klub}</div>}
                            {hrac.sutaz && (
                              <div className="text-xs">{hrac.sutaz}</div>
                            )}
                          </div>
                          {hrac.sezona_zapasy > 0 && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground/60">
                              <Activity className="h-3 w-3" />
                              {hrac.sezona_zapasy} zápasov, {hrac.sezona_goly}G{" "}
                              {hrac.sezona_asistencie}A
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </motion.section>
  );
}
